import { supabase } from '@/integrations/supabase/client';
import { AdminUser, SearchUsersParams, SearchUsersResult } from '@/contracts/adminUsers';

export interface UserProfile {
  id: string;
  user_id: string;
  email: string | null;
  is_pro_member: boolean;
  created_at: string;
  roles: {
    admin: boolean;
    editor: boolean;
  };
}

/**
 * Get all user profiles with roles
 */
export async function getUsers(): Promise<UserProfile[]> {
  const [profilesResult, rolesResult] = await Promise.all([
    supabase.from('profiles').select('*').order('created_at', { ascending: false }),
    supabase.from('user_roles').select('*'),
  ]);

  if (profilesResult.error) throw profilesResult.error;
  if (rolesResult.error) throw rolesResult.error;

  const profiles = profilesResult.data || [];
  const roles = rolesResult.data || [];

  return profiles.map((profile) => ({
    id: profile.id,
    user_id: profile.user_id,
    email: profile.email,
    is_pro_member: profile.is_pro_member,
    created_at: profile.created_at,
    roles: {
      admin: roles.some((r) => r.user_id === profile.user_id && r.role === 'admin'),
      editor: roles.some((r) => r.user_id === profile.user_id && r.role === 'editor'),
    },
  }));
}

/**
 * Toggle pro membership status
 */
export async function toggleProMembership(
  userId: string,
  currentStatus: boolean
): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update({ is_pro_member: !currentStatus })
    .eq('user_id', userId);

  if (error) throw error;
}

/**
 * Add role to user
 */
export async function addRole(userId: string, role: 'admin' | 'editor'): Promise<void> {
  const { error } = await supabase
    .from('user_roles')
    .insert({ user_id: userId, role });

  if (error) throw error;
}

/**
 * Remove role from user
 */
export async function removeRole(userId: string, role: 'admin' | 'editor'): Promise<void> {
  const { error } = await supabase
    .from('user_roles')
    .delete()
    .eq('user_id', userId)
    .eq('role', role);

  if (error) throw error;
}

/**
 * Search users with server-side pagination and filtering
 */
export async function searchUsers(params: SearchUsersParams): Promise<SearchUsersResult> {
  const {
    search = '',
    filterPro = null,
    filterRole = null,
    page = 1,
    pageSize = 25,
  } = params;

  const offset = (page - 1) * pageSize;

  const { data, error } = await supabase.rpc('search_users', {
    p_search_term: search || null,
    p_filter_pro: filterPro,
    p_filter_role: filterRole,
    p_limit: pageSize,
    p_offset: offset,
  });

  if (error) {
    console.error('search_users RPC error', error);
    throw error;
  }

  const rows = (data ?? []) as any[];
  const totalCount = rows.length > 0 ? Number(rows[0].total_count) : 0;

  const users: AdminUser[] = rows.map(row => ({
    id: row.id,
    user_id: row.user_id,
    email: row.email,
    full_name: row.full_name,
    is_pro_member: row.is_pro_member,
    is_vendor: row.is_vendor,
    created_at: row.created_at,
    roles: row.roles ?? [],
  }));

  return { users, totalCount };
}
